import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useState, useEffect, useCallback } from 'react';

import { LoadingInner } from '~/_legacy/components/molecules/loading/LoadingInner';

import {
  SearchOrganizationsParams,
  useApiGetV2SearchOrganizations,
} from '~/api/v2/searches/organizations/get';
import { Breadcrumb } from '~/components/Breadcrumb/Breadcrumb';
import { CardOrganization } from '~/components/Card/CardOrganization';
import { SearchInput } from '~/components/Form/Input/SearchInput';
import { ItemCountLabel } from '~/components/ItemCountLabel/ItemCountLabel';
import { Pagination } from '~/components/Pagination/Pagination';
import Layout from '~/pages/_layout.page';
import { isEmpty } from '~/utils/array';

import { getParamsFromQuery, useOnPopState } from './_utils';

const routes = [
  {
    path: '/',
    breadcrumbName: 'TOP',
  },
  {
    path: '/orgs',
    breadcrumbName: 'NPO・社会的事業者',
  },
];

export default function Page() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const initialParams = getParamsFromQuery(searchParams);
  const [searchText, setSearchText] = useState(() => initialParams.keyword || '');
  const [paramsForSearch, setParamsForSearch] = useState(initialParams);
  const [previousQuery, setPreviousQuery] = useState('');

  // 1ページあたりの企業数
  const organizationsPerPage = 12;
  // ページネーションの現在のページ番号
  const [currentPage, setCurrentPage] = useState<number>(1);
  // 検索条件に応じて変更されるため、総ページ数は状態として管理する
  const [totalPages, setTotalPages] = useState<number>(1);

  const {
    orgs: organizations,
    totalCount,
    isLoading,
    initializeObserver,
  } = useApiGetV2SearchOrganizations(paramsForSearch);

  useEffect(() => {
    setSearchText(initialParams.keyword || '');
  }, [initialParams.keyword]);

  useOnPopState(() => {
    const params = getParamsFromQuery(searchParams);
    setParamsForSearch(params);
    setSearchText(params.keyword || '');
  });

  const search = (nextParams: SearchOrganizationsParams) => {
    router.push({ pathname: '/orgs' }, { query: nextParams }, { shallow: true });
    setParamsForSearch(nextParams);
  };

  const handleSearch = useCallback(
    (nextParams: SearchOrganizationsParams) => {
      if (searchText.trim() === '') {
        setPreviousQuery('');
        search(nextParams);
      } else if (searchText !== previousQuery) {
        setPreviousQuery(searchText);
        search(nextParams);
      }
    },
    [searchText, previousQuery],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing || e.key !== 'Enter') return;

    if (e.key === 'Enter') {
      handleSearch({ ...paramsForSearch, keyword: searchText });
    }
  };

  const handleBlur = () => {
    handleSearch({ ...paramsForSearch, keyword: searchText });
  };

  // 検索フォームが変更されたとき
  function handleChangeInput(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchText(e.target.value);
  }

  // ページネーションのページが変更されたとき
  function handlePageChange(newPage: number) {
    setCurrentPage(newPage);
    setParamsForSearch((prevParams) => ({ ...prevParams, page: newPage }));
  }

  useEffect(() => {
    if (!organizations) return;

    setTotalPages(Math.ceil(totalCount / organizationsPerPage));
  }, [organizations]);

  function OrganizationListComponent() {
    // - SPO一覧取得APIを呼び出し中（`isLoading`）の場合
    if (isLoading) {
      return (
        <div
          className='flex flex-col items-center justify-center gap-l__sp lg:gap-l'
          ref={initializeObserver}
        >
          <LoadingInner isSmallSpinner height='100px' width='100px' />
        </div>
      );
    }

    // - SPO一覧取得ができなかった、または0件の場合
    if (isEmpty(organizations)) {
      return <div>SPO一覧が取得できません</div>;
    }

    // - SPO一覧の検索結果が0件の場合
    if (isEmpty(organizations)) {
      return <div>検索結果が0件です</div>;
    }

    // - SPO一覧の検索結果が1件以上の場合
    return (
      <ul className='flex flex-col gap-l__sp lg:gap-l'>
        {organizations.map((organization, index) => (
          <li key={`organization-${index}`}>
            <CardOrganization
              organizationName={organization.name}
              vision={''}
              logo={organization.logo ?? '/icons/corp-icon.png'}
              uid={organization.uid}
              area={organization.area}
              tags={organization.tags?.map((tag) => tag.name)}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Layout pageTitle={'NPO・社会的事業者'} pageDescription={'NPO・社会的事業者'}>
      <main className='flex w-full max-w-[960px] flex-col items-center gap-m__sp px-xl__sp pb-10 lg:gap-m lg:px-xl'>
        {/* NOTE: パンくずリスト */}
        <div className='flex w-full items-center py-l__sp lg:py-l'>
          <Breadcrumb routes={routes} />
        </div>

        <div className='lg:flex lg:w-full lg:gap-xxl'>
          {/* NOTE: title（SP） */}
          <div className='flex w-full items-center pb-2.5 lg:hidden lg:pb-0'>
            <h2 className='text-title-x-large__sp font-bold text-primary'>NPO・社会的事業者</h2>
          </div>

          {/* NOTE: 検索条件（PC） */}
          <div className='hidden w-[240px] lg:block'>
            <div className='hidden flex-col lg:flex lg:gap-s'>
              <h3 className='text-primary lg:text-title-small lg:font-bold'>検索条件</h3>
              <div>
                <SearchInput
                  placeholder='キーワードで探す'
                  value={searchText}
                  onChange={handleChangeInput}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                />
              </div>
            </div>
          </div>

          <div className='flex w-full flex-col gap-xl__sp lg:flex-1 lg:gap-xl'>
            {/* NOTE: 検索条件（SP） */}
            <div className='flex flex-col gap-s__sp lg:hidden'>
              <h3 className='text-title-small__sp font-bold text-primary lg:text-title-small lg:font-bold'>
                検索条件
              </h3>
              <div>
                <SearchInput
                  placeholder='キーワードで探す'
                  value={searchText}
                  onChange={handleChangeInput}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                />
              </div>
            </div>

            {/* NOTE: title（PC） */}
            <div className='hidden w-full items-center lg:flex'>
              <h2 className='text-primary lg:text-title-x-large lg:font-bold'>NPO・社会的事業者</h2>
            </div>

            {/* NOTE: NPO・社会的事業者 */}
            <div className='flex flex-col gap-l__sp lg:flex-row lg:gap-l'>
              <ItemCountLabel total={totalCount} />
              <div className='hidden lg:block'>
                <Pagination total={totalPages} current={currentPage} onChange={handlePageChange} />
              </div>
            </div>
            <OrganizationListComponent />
            <div className='flex flex-col gap-l__sp lg:flex-row lg:gap-l'>
              <ItemCountLabel total={totalCount} />
              <Pagination total={totalPages} current={currentPage} onChange={handlePageChange} />
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
}
